import { useAppStore } from "@/store/app";
import { useSessionStore } from "@/store/session";
import axios, { AxiosHeaders } from "axios";
import { ApiError } from "@/types/api-error";
import { getOrCreateDeviceId } from "./app-util";

export const instance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },

  params: {}
});

let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

// Request interceptor: add Authorization token
instance.interceptors.request.use(
  config => {
    const store = useSessionStore();
    const token = store.accessToken;
    config.headers = config.headers || new AxiosHeaders();

    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    config.headers["X-Device-Id"] = getOrCreateDeviceId();
    return config;
  },
  error => Promise.reject(error)
);

instance.interceptors.response.use(
  response => response,
  async error => {
    const store = useSessionStore();
    const status = error?.response?.status;
    const originalRequest = error.config;
    // Prevent refresh on login/refresh endpoints
    if (
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    // Only handle 401
    if (status !== 401) {
      return Promise.reject(error);
    }

    // Prevent infinite loop
    if (originalRequest._retry) {
      store.logout?.();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // If refresh already in progress → wait
    if (isRefreshing) {
      try {
        await refreshPromise;
        originalRequest._retry = true;
        return instance(originalRequest);
      } catch (e) {
        useSessionStore().logout?.();
        window.location.href = "/login";
        return Promise.reject(e);
      }
    }

    // Start refresh flow
    originalRequest._retry = true;
    isRefreshing = true;
    refreshPromise = store.refreshToken();

    try {      

      await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;

      // Retry original request with new token
      return instance(originalRequest);

    } catch (refreshError) {
      isRefreshing = false;
      refreshPromise = null;

      // YES — redirect when refresh fails
      store.logout?.();
      window.location.href = "/login";
      return Promise.reject(refreshError);
    }
  }
);

export function handleErrorAndRethrow(error: unknown): never {
  const message = extractErrorMessage(error);
  useAppStore().sendErrorNotice(message);
  throw error;
}

function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Server unreachable. Please try again later.";
    }

    const data = error.response.data;

    if (typeof data === "object" && data && "status" in data && "message" in data) {
      const apiError = data as ApiError;
      return `${apiError.status}: ${apiError.message}`;
    }

    return `${error.response.status}: Unexpected server error`;
  }

  return error instanceof Error ? error.message : "An unexpected error occurred.";
}


// ---------------------------
// Generic request wrapper
// ---------------------------
async function apiRequest<T>(
  request: Promise<T>
): Promise<T | null> {
  try {
    return await request;
  } catch (error) {
    handleErrorAndRethrow(error);
  }
}

// ---------------------------
// API methods
// ---------------------------
export async function getData<T>(url: string): Promise<T | null> {
  const response = await apiRequest(instance.get<T>(url));
  if (!response) return null;
  return response.data;
}

export async function postData<T>(
  url: string,
  data?: any
): Promise<T | null> {
  const response = await apiRequest(instance.post<T>(url, data));
  if (!response) return null;
  return response.data;
}

export async function putData<T>(
  url: string,
  data?: any
): Promise<T | null> {
  const response = await apiRequest(instance.put<T>(url, data));
  if (!response) return null;
  return response.data;
}

export async function deleteData<T>(
  url: string
): Promise<T | null> {
  const response = await apiRequest(instance.delete<T>(url));
  if (!response) return null;
  return response.data;
}